import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PhoneHoursScheduleRule,
  ScheduleRule,
  ScheduleType,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { genUUIDV4 } from '@frontend/string';
import { useForm, FieldConfigs } from '@frontend/design-system';
import { BreakRules, BreakSchedulesByDay } from './phone-hours-form';

export type Break = { startTime: string; endTime: string; breakName: string; id: string };
export type DaysOfWeek = (typeof DAYS_OF_WEEK)[number];
export const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

type DayNames = (typeof DAYS_OF_WEEK)[number];

type PhoneHoursScheduleFields = {
  [key in `selected-${DayNames}`]: FieldConfigs['checkbox'];
} & {
  [key in `range-${DayNames}`]: FieldConfigs['timeRange'];
};

const createTimeFields = (
  days: readonly DayNames[],
  scheduleRules: ScheduleRule[],
  phoneHours: PhoneHoursScheduleRule[]
): PhoneHoursScheduleFields => {
  const fields = {} as PhoneHoursScheduleFields;
  const isNewSchedule = !phoneHours.length; // Check if this is the first creation

  days.forEach((day, i) => {
    const range = ['9:00', '17:00'];
    const rule = scheduleRules.find((scheduleRule) => scheduleRule.dayOfWeek === i);
    if (rule?.startTime) {
      range[0] = rule.startTime;
    }

    if (rule?.endTime) {
      range[1] = rule.endTime;
    }

    // Default to true for Monday through Friday (index 1 to 5) only on first creation
    const isDefaultChecked = isNewSchedule && i >= 1 && i <= 5;

    fields[`selected-${day}`] = { type: 'checkbox', value: !!rule || isDefaultChecked };
    fields[`range-${day}`] = {
      type: 'timeRange',
      interval: 60,
      value: range,
      required: true,
    };
  });

  return fields;
};

export const usePhoneHoursForm = ({
  phoneHours,
  initialName = '',
  keepNewIds = false,
}: {
  phoneHours: PhoneHoursScheduleRule[];
  initialName?: string;
  keepNewIds?: boolean;
}) => {
  const openScheduleRules = useMemo(() => phoneHours.find((hours) => hours.type === 'OPEN')?.rules ?? [], [phoneHours]);
  const existingBreaks = useMemo(() => phoneHours.filter((item) => item.type === 'BREAK'), [phoneHours]);

  // The useForm hook to set up the form fields for the days of the week that are open to control the
  // time ranges for each day. (This does not include the breaks time ranges).
  const openSchedulesForm = useForm({
    fields: {
      ...createTimeFields(DAYS_OF_WEEK, openScheduleRules, phoneHours),
    },
  });

  // Form field for phone hours name
  const nameForm = useForm({
    fields: {
      name: {
        type: 'text' as const,
        value: initialName,
        required: true,
      },
    },
  });

  // The state to hold the break schedules. This is a map of the break schedules by their scheduleId.
  const [breakSchedulesMap, setBreakSchedules] = useState<BreakRules>({});

  // The breakSchedulesByDay is a map of the break schedules by day of the week. This is used to conveniently
  // access the break schedules for each day of the week. The keys are the days of the week and the values
  // are the break schedules for that day.
  const breakSchedulesByDay = useMemo(() => {
    return Object.entries(breakSchedulesMap).reduce((acc, [id, breakItem]) => {
      const { rules, name } = breakItem;
      rules?.forEach((rule) => {
        const day = DAYS_OF_WEEK[rule.dayOfWeek];
        if (!acc[day]) {
          acc[day] = [];
        }
        acc[day].push({
          startTime: rule.startTime,
          endTime: rule.endTime,
          breakName: name,
          id,
        });
      });
      return acc;
    }, {} as BreakSchedulesByDay);
  }, [breakSchedulesMap]);

  // The available break names are the unique names of the breaks that are available to be selected
  // for the breaks on the phone hours form. This is used to populate the dropdowns for the break names.
  const availableBreakNames = useMemo(() => {
    const uniqueNames = new Map<string, string>();

    Object.values(breakSchedulesMap).forEach((item) => {
      const trimmedName = item.name?.trim();
      if (!trimmedName) {
        return;
      }

      const normalizedName = trimmedName.toLowerCase();
      if (!uniqueNames.has(normalizedName)) {
        uniqueNames.set(normalizedName, trimmedName);
      }
    });

    return Array.from(uniqueNames.values());
  }, [breakSchedulesMap]);

  useEffect(() => {
    if (!existingBreaks.length) {
      return;
    }

    // Initialize the break schedules state with the existing breaks from the phone hours.
    // Set up a map to hold the breaks by their scheduleId.
    const breaksMap: BreakRules = {};

    existingBreaks.forEach((breakItem) => {
      // Copy the break item to avoid mutating the original object
      breaksMap[breakItem.scheduleId] = {
        ...breakItem,
        rules: [...(breakItem.rules ?? [])],
      };
    });

    setBreakSchedules((prev) => {
      // Avoid clobbering local unsaved breaks when parent data refreshes during editing.
      const hasUnsavedNewBreaks = Object.keys(prev).some((id) => id.startsWith('new:'));
      if (hasUnsavedNewBreaks) {
        return prev;
      }

      return breaksMap;
    });
  }, [existingBreaks]);

  // Function to generate a new id for the break schedules. This is used when the user creates a new break.
  // The new id is prefixed with 'new:' to indicate that it is a new break that has not been saved yet.
  // This is used to differentiate between the new breaks and the existing breaks that are loaded from the BE.
  // Other places in the code will check if the id starts with 'new:' to determine if it is a new break or an
  // existing break.
  const genNewId = () => 'new:' + genUUIDV4();

  // Called when the user changes the break name or time range for a break on a specific day.
  const handleBreakTimeChange = useCallback(
    ({
      day,
      startTime,
      endTime,
      id,
    }: {
      day: DaysOfWeek;
      startTime: string;
      endTime: string;
      previousName?: string;
      id: string;
    }) => {
      setBreakSchedules((prev) => {
        const updatedBreaks = { ...prev };

        const currentBreak = updatedBreaks[id];
        if (!currentBreak) {
          console.warn(`Break with id ${id} not found`);
          return prev;
        }

        updatedBreaks[id] = {
          ...currentBreak,
          rules: (currentBreak?.rules ?? []).map((rule) => {
            if (rule.dayOfWeek === DAYS_OF_WEEK.indexOf(day)) {
              return { ...rule, startTime, endTime };
            }
            return rule;
          }),
        };

        return updatedBreaks;
      });
    },
    []
  );

  // Replaces a break with a new break.
  const handleBreakNameChangeNew = useCallback(
    ({ day, breakName, id }: { day: DaysOfWeek; id: string; breakName: string }) => {
      setBreakSchedules((prev) => {
        const updatedBreaks = { ...prev };
        const currentBreak = updatedBreaks[id];

        if (!currentBreak) {
          console.warn(`Break with id ${id} not found`);
          return prev;
        }

        if (currentBreak.name === '') {
          // If the current break has no name, then this means that this is a temporary break
          // that was created and the user is now trying to set a name for it. Since here the user
          // is creating a new break, we can just repurpose the current temporary break as the new
          // break with the new name.
          updatedBreaks[id] = {
            ...currentBreak,
            name: breakName,
          };
          return updatedBreaks;
        } else {
          const dayOfWeek = DAYS_OF_WEEK.indexOf(day);
          const ruleForDay = (currentBreak.rules ?? []).find((rule) => rule.dayOfWeek === dayOfWeek);

          // If we cannot find a rule for this day, keep current state to avoid dropping the visible row.
          if (!ruleForDay) {
            console.warn(`No break rule found for day ${day} on break id ${id}`);
            return prev;
          }

          // We need to create a new break with the specified name and the current break's rules for the specified day.
          // We also need to remove the rule from the current break.
          const newBreakId = genNewId();
          updatedBreaks[newBreakId] = {
            name: breakName,
            scheduleId: newBreakId,
            type: ScheduleType.BREAK,
            rules: [ruleForDay],
          };

          if (updatedBreaks[id]?.rules) {
            // Remove the rule from the current break
            updatedBreaks[id].rules = updatedBreaks[id].rules.filter((rule) => rule.dayOfWeek !== dayOfWeek);
          }

          return updatedBreaks;
        }
      });

      return;
    },
    []
  );

  // Replaces a break with another existing break.
  const handleBreakNameChangeReplace = ({ day, breakName, id }: { day: DaysOfWeek; id: string; breakName: string }) => {
    setBreakSchedules((prev) => {
      const updatedBreaks = { ...prev };
      const currentBreak = updatedBreaks[id];

      if (!currentBreak) {
        console.warn(`Break with id ${id} not found`);
        return prev;
      }

      if (currentBreak.name === breakName) {
        // No need to update if the name is the same
        return updatedBreaks;
      }

      // Find the corresponding break object with the same name. This will be the break that
      // is being updated to contain the existing break's rules for the specified day.
      const existingBreak = Object.values(updatedBreaks).find((breakItem) => breakItem.name === breakName);
      if (!existingBreak) {
        console.error('No existing break found with the same name');
        return updatedBreaks;
      }

      if (currentBreak.name === '') {
        // If the current name is empty, then this means that this is a temporary break
        // that was created and the user is now trying to set a name for it. So we need to
        // find the corresponding break that has the same name and update it so that it has
        // a new rule based on the temporary break. We also need to remove the temporary break.
        const existingBreakId = existingBreak.scheduleId;
        const rules = [...(existingBreak.rules ?? []), ...(currentBreak.rules ?? [])];
        updatedBreaks[existingBreakId].rules = rules;
        delete updatedBreaks[id];
        return updatedBreaks;
      } else {
        const dayOfWeek = DAYS_OF_WEEK.indexOf(day);
        const sourceRuleForDay = (currentBreak.rules ?? []).find((rule) => rule.dayOfWeek === dayOfWeek);

        // If this break has no rule for the selected day, there is nothing to replace.
        if (!sourceRuleForDay) {
          return updatedBreaks;
        }

        // At this point, we know that the current break has a name and the user is trying to
        // update the name to an existing break's name. So we need to find the corresponding
        // break that has the same name and update it so that it has a new rule based on the
        // current break. We also need to remove the rule from the current break.
        const existingBreakId = existingBreak.scheduleId;
        const rules = [
          // keep the existing rules for the break
          ...(existingBreak.rules ?? []),
          // add the current break's rule for the specified day
          sourceRuleForDay,
        ];
        updatedBreaks[existingBreakId].rules = rules;

        if (updatedBreaks[id]?.rules) {
          // Remove the rule from the current break
          updatedBreaks[id].rules = updatedBreaks[id].rules.filter((rule) => rule.dayOfWeek !== dayOfWeek);
        }

        return updatedBreaks;
      }
    });
  };

  // Called when the user changes the break for a specific day.
  const handleBreakChange = useCallback(
    ({ day, breakName, id, isNew = false }: { day: DaysOfWeek; id: string; breakName: string; isNew?: boolean }) => {
      if (isNew) {
        handleBreakNameChangeNew({ day, breakName, id });
      } else {
        handleBreakNameChangeReplace({ day, breakName, id });
      }
    },
    [handleBreakNameChangeNew]
  );

  // Called when the user clicks on the plus icon button to add a new break slot to one of the days.
  // NOTE: this is not the same as creating a new break (with a new name).
  const handleAddBreak = useCallback((day: DaysOfWeek) => {
    setBreakSchedules((prev) => {
      const updatedBreaks = { ...prev };

      const newBreakId = genNewId();
      updatedBreaks[newBreakId] = {
        name: '',
        scheduleId: newBreakId,
        type: ScheduleType.BREAK,
        rules: [{ startTime: '12:00', endTime: '13:00', dayOfWeek: DAYS_OF_WEEK.indexOf(day) }],
      };
      return updatedBreaks;
    });
  }, []);

  const handleDuplicateBreak = useCallback(
    ({ days, id, sourceDay }: { days: DaysOfWeek[]; id: string; sourceDay: DaysOfWeek }) => {
      const sourceDayIndex = DAYS_OF_WEEK.indexOf(sourceDay);

      setBreakSchedules((prev) => {
        const currentBreak = prev[id];
        if (!currentBreak?.rules) return prev;

        const ruleToDuplicate = currentBreak.rules.find((rule) => rule.dayOfWeek === sourceDayIndex);
        if (!ruleToDuplicate) return prev;

        const newRules = currentBreak.rules
          /**
           * 1. Filter out rules for days that are going to be modified (we will replace them with the new rule)
           * 2. Keep the existing rules for the source day (we never want to modify the source day's rule)
           */
          .filter((rule) => !days.includes(DAYS_OF_WEEK[rule.dayOfWeek]) || rule.dayOfWeek === sourceDayIndex)
          /**
           * 3. Create new rules for the duplicated days based on the source day's rule
           */
          .concat(
            days.map((day) => ({
              ...ruleToDuplicate,
              dayOfWeek: DAYS_OF_WEEK.indexOf(day),
            }))
          );

        return {
          ...prev,
          [id]: {
            ...currentBreak,
            rules: newRules,
          },
        };
      });

      // Add and remove class to highlight duplicated days
      days
        .filter((d) => d !== sourceDay)
        .forEach((day) => {
          document.querySelector(`.day-container-${day}`)?.classList.add('duplicated');
          setTimeout(() => {
            document.querySelector(`.day-container-${day}`)?.classList.remove('duplicated');
          }, 1500);
        });
    },
    []
  );

  // Called when the user removes a break slot from one of the days.
  // NOTE: this does not delete the break itself, it just removes the rule for the specified day.
  const handleRemoveBreak = useCallback(({ day, id }: { day: DaysOfWeek; id: string }) => {
    setBreakSchedules((prev) => {
      const updatedBreaks = { ...prev };

      const currentBreak = updatedBreaks[id];

      if (!currentBreak) return prev;

      // Remove the rule for the specific day
      const updatedRules = (currentBreak.rules ?? []).filter((rule) => rule.dayOfWeek !== DAYS_OF_WEEK.indexOf(day));

      // If it's a new break and now has no rules and empty name, delete it
      if (id.startsWith('new:') && currentBreak.name.trim() === '' && updatedRules.length === 0) {
        delete updatedBreaks[id];
      } else {
        // Otherwise, update the rules
        updatedBreaks[id] = {
          ...currentBreak,
          rules: updatedRules,
        };
      }

      return updatedBreaks;
    });
  }, []);

  // Called when the user clicks on the edit icon button to edit a break's name.
  const handleEditBreak = useCallback((id: string, newName: string) => {
    setBreakSchedules((prev) => {
      const updatedBreaks = { ...prev };
      const currentBreak = updatedBreaks[id];

      updatedBreaks[id] = {
        ...currentBreak,
        name: newName,
      };

      return updatedBreaks;
    });
  }, []);

  // Called when the user clicks on the delete icon button to completely remove a break from the list
  // of breaks so that it is no longer available to be added to any of the days.
  const handleDeleteBreak = useCallback((id: string) => {
    setBreakSchedules((prev) => {
      const updatedBreaks = { ...prev };
      delete updatedBreaks[id];

      return updatedBreaks;
    });
  }, []);

  const getPhoneHours = useCallback((): PhoneHoursScheduleRule[] => {
    // Build the open schedules
    const openScheduleId = phoneHours.find((item) => item.type === 'OPEN')?.scheduleId ?? '';
    const openSchedules: PhoneHoursScheduleRule[] = [
      {
        type: ScheduleType.OPEN,
        name: 'Open',
        scheduleId: openScheduleId,
        rules: Object.entries(openSchedulesForm.values).reduce((acc, [key, value]) => {
          if (key.startsWith('selected-') && value) {
            const day = key.split('-')[1] as DaysOfWeek;
            const rangeKey = `range-${day}` as `range-${DaysOfWeek}`;
            const [startTime, endTime] = openSchedulesForm.values[rangeKey] ?? ['', ''];
            acc.push({ dayOfWeek: DAYS_OF_WEEK.indexOf(day), startTime, endTime });
          }
          return acc;
        }, [] as ScheduleRule[]),
      },
    ];

    if (!keepNewIds) {
      // If we are not keeping the new ids, then we need to empty (set to empty string) any new ids that were
      // generated on the FE from the break schedules. The new ids are the ones that start with 'new:'.
      Object.entries(breakSchedulesMap).forEach(([id, breakItem]) => {
        if (id.startsWith('new:')) {
          breakItem.scheduleId = '';
        }
      });
    }

    // combine the open schedules with the break schedules
    const combinedSchedules = [...openSchedules, ...Object.values(breakSchedulesMap)];

    return combinedSchedules;
  }, [phoneHours, openSchedulesForm.values, keepNewIds, breakSchedulesMap]);

  const [hasAnyBreakConflict, setHasAnyBreakConflict] = useState(false);

  return {
    getPhoneHours,
    hasAnyBreakConflict,
    formProps: {
      nameForm,
      openSchedulesForm,
      breakSchedules: breakSchedulesMap,
      breakSchedulesByDay,
      availableBreakNames,
      onBreakTimeChange: handleBreakTimeChange,
      onBreakChange: handleBreakChange,
      onBreakRemove: handleRemoveBreak,
      onBreakAdd: handleAddBreak,
      onEditBreak: handleEditBreak,
      onDeleteBreak: handleDeleteBreak,
      onDuplicateBreak: handleDuplicateBreak,
      setHasAnyBreakConflict,
    },
  };
};
